'use client'

import { useLocale } from '@/components/LocaleContext'
import { useRouter } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'
import { LuArrowUpLeft, LuArrowUpRight } from 'react-icons/lu'
import { toast } from 'sonner'

import { CountrySelect } from '@/components/frontend/CountrySelect'
import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from '@/components/shared/TurnstileWidget'
import { getStoredProfile } from '@/lib/profile'
import {
  isValidContactCompany,
  isValidContactEmail,
  isValidContactMessage,
  isValidContactName,
  sanitizeContactCompanyInput,
  sanitizeContactNameInput,
} from '@/lib/utils'
import {
  MAX_COMPANY_LENGTH,
  MIN_COMPANY_LENGTH,
  MIN_MESSAGE_LENGTH,
  PHONE_DIGITS_ONLY_REGEX,
} from '@/utilities/constant'
import { COUNTRIES } from '@/utilities/countries'

type SubmitStatus = 'idle' | 'sending' | 'error'

function getApiErrorMessage(data: Record<string, unknown>, fallback: string): string {
  if (typeof data.error === 'string') return data.error
  if (data.error && typeof data.error === 'object') {
    const nested = data.error as Record<string, unknown>
    if (nested.message && typeof nested.message === 'object') {
      const message = (nested.message as { message?: string }).message
      if (typeof message === 'string') return message
    }
    if (typeof nested.message === 'string') return nested.message
  }
  if (data.success === false && typeof data.message === 'string') return data.message
  return fallback
}

export type FieldErrors = Record<string, string>

type ContactFormValues = {
  firstName: string
  lastName: string
  email: string
  company: string
  contactNumber: string
  message: string
}

const EMPTY_FORM_VALUES: ContactFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  contactNumber: '',
  message: '',
}

function getProfilePrefill(): Pick<
  ContactFormValues,
  'firstName' | 'lastName' | 'email' | 'company' | 'contactNumber'
> & { countryCode: string } {
  const profile = getStoredProfile()
  const countryHint = profile.region.trim() || profile.location.trim()
  let countryCode = ''

  if (countryHint) {
    const match = COUNTRIES.find(
      (country) =>
        country.name.toLowerCase() === countryHint.toLowerCase() ||
        country.code.toLowerCase() === countryHint.toLowerCase(),
    )
    if (match) countryCode = match.code
  }

  let contactNumber = profile.phoneNumber.replace(/\s/g, '')
  if (contactNumber.startsWith('+')) {
    const byDialCode = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length)
    for (const country of byDialCode) {
      if (contactNumber.startsWith(country.dialCode)) {
        if (!countryCode) countryCode = country.code
        contactNumber = contactNumber.slice(country.dialCode.length).replace(/\D/g, '')
        break
      }
    }
  } else {
    contactNumber = contactNumber.replace(/\D/g, '')
  }

  return {
    firstName: profile.firstName || profile.name || '',
    lastName: profile.lastName || '',
    email: profile.email || '',
    company: profile.company || '',
    contactNumber,
    countryCode,
  }
}

const CONTACT_FORM_TEXT: Record<
  AppLocale,
  {
    sendYourMessage: string
    requestProduct: string
    successMessage: string
    failedToSend: string
    firstNameRequired: string
    firstNameInvalid: string
    lastNameRequired: string
    lastNameInvalid: string
    emailRequired: string
    emailInvalid: string
    companyRequired: string
    companyTooLong: string
    companyInvalid: string
    countryRequired: string
    phoneRequired: string
    phoneInvalid: string
    messageRequired: string
    messageMinLength: (min: number) => string
    messageInvalid: string
    firstName: string
    lastName: string
    email: string
    company: string
    country: string
    selectCountry: string
    phoneNumber: string
    message: string
    sending: string
    submit: string
    turnstileRequired: string
    turnstileFailed: string
    messagePlaceholder: (searchTerm: string) => string
  }
> = {
  en: {
    sendYourMessage: 'SEND YOUR MESSAGE',
    requestProduct: 'Request Missing Product',
    successMessage: 'Thank you. Your message has been sent successfully.',
    failedToSend: 'Failed to send message. Please try again.',
    firstNameRequired: 'First name is required.',
    firstNameInvalid: 'First name can only contain letters, spaces, hyphens and apostrophes.',
    lastNameRequired: 'Last name is required.',
    lastNameInvalid: 'Last name can only contain letters, spaces, hyphens and apostrophes.',
    emailRequired: 'Email is required.',
    emailInvalid: 'Please enter a valid email address that includes at least one letter.',
    companyRequired: 'Company name is required.',
    companyTooLong: 'Company name cannot exceed 80 characters.',
    companyInvalid:
      'Company name must include at least one letter. Letters, numbers and symbols are allowed.',
    countryRequired: 'Country is required.',
    phoneRequired: 'Phone number is required.',
    phoneInvalid: 'Phone number can only contain numbers.',
    messageRequired: 'Message is required.',
    messageMinLength: (min) => `Message must be at least ${min} characters.`,
    messageInvalid:
      'Message must include at least one letter. Numbers and symbols alone are not allowed.',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    company: 'Company',
    country: 'Country',
    selectCountry: 'Select country',
    phoneNumber: 'Phone Number',
    message: 'Message',
    sending: 'Sending…',
    submit: 'SUBMIT',
    turnstileRequired: 'Please complete the security verification before submitting.',
    turnstileFailed: 'Security verification failed. Please try again.',
    messagePlaceholder: (searchTerm: string) => `I'm interested in the product: ${searchTerm}.`,
  },
  fr: {
    sendYourMessage: 'ENVOYEZ VOTRE MESSAGE',
    requestProduct: 'Demander un produit manquant',
    successMessage: 'Merci. Votre message a été envoyé avec succès.',
    failedToSend: "Échec de l'envoi du message. Veuillez réessayer.",
    firstNameRequired: 'Le prénom est requis.',
    firstNameInvalid:
      'Le prénom peut contenir uniquement des lettres, espaces, tirets et apostrophes.',
    lastNameRequired: 'Le nom est requis.',
    lastNameInvalid: 'Le nom peut contenir uniquement des lettres, espaces, tirets et apostrophes.',
    emailRequired: "L'adresse e-mail est requise.",
    emailInvalid: 'Veuillez saisir une adresse e-mail valide contenant au moins une lettre.',
    companyRequired: 'Le nom de la société est requis.',
    companyTooLong: 'Le nom de la société ne peut pas dépasser 80 caractères.',
    companyInvalid:
      'Le nom de la société doit contenir au moins une lettre. Lettres, chiffres et symboles sont autorisés.',
    countryRequired: 'Le pays est requis.',
    phoneRequired: 'Le numéro de téléphone est requis.',
    phoneInvalid: 'Le numéro de téléphone peut contenir uniquement des chiffres.',
    messageRequired: 'Le message est requis.',
    messageMinLength: (min) => `Le message doit comporter au moins ${min} caractères.`,
    messageInvalid:
      'Le message doit contenir au moins une lettre. Les chiffres et symboles seuls ne sont pas autorisés.',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'E-mail',
    company: 'Société',
    country: 'Pays',
    selectCountry: 'Sélectionner un pays',
    phoneNumber: 'Numéro de téléphone',
    message: 'Message',
    sending: 'Envoi…',
    submit: 'ENVOYER',
    turnstileRequired:
      'Veuillez compléter la vérification de sécurité avant de soumettre.',
    turnstileFailed: 'La vérification de sécurité a échoué. Veuillez réessayer.',
    messagePlaceholder: (searchTerm: string) =>
      `Je suis intéressé(e) par le produit : ${searchTerm}.`,
  },
  ar: {
    sendYourMessage: 'أرسل رسالتك',
    requestProduct: 'طلب منتج مفقود',
    successMessage: 'شكرًا لك. تم إرسال رسالتك بنجاح.',
    failedToSend: 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.',
    firstNameRequired: 'الاسم الأول مطلوب.',
    firstNameInvalid: 'يمكن أن يحتوي الاسم الأول على أحرف ومسافات وشرطات وفواصل فقط.',
    lastNameRequired: 'اسم العائلة مطلوب.',
    lastNameInvalid: 'يمكن أن يحتوي اسم العائلة على أحرف ومسافات وشرطات وفواصل فقط.',
    emailRequired: 'البريد الإلكتروني مطلوب.',
    emailInvalid: 'يرجى إدخال بريد إلكتروني صحيح يحتوي على حرف واحد على الأقل.',
    companyRequired: 'اسم الشركة مطلوب.',
    companyTooLong: 'اسم الشركة يجب ألا يتجاوز 80 حرفًا.',
    companyInvalid:
      'يجب أن يحتوي اسم الشركة على حرف واحد على الأقل. يُسمح بالأحرف والأرقام والرموز.',
    countryRequired: 'الدولة مطلوبة.',
    phoneRequired: 'رقم الهاتف مطلوب.',
    phoneInvalid: 'يمكن أن يحتوي رقم الهاتف على أرقام فقط.',
    messageRequired: 'الرسالة مطلوبة.',
    messageMinLength: (min) => `يجب أن تحتوي الرسالة على ${min} أحرف على الأقل.`,
    messageInvalid:
      'يجب أن تحتوي الرسالة على حرف واحد على الأقل. الأرقام والرموز وحدها غير مسموحة.',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    company: 'الشركة',
    country: 'الدولة',
    selectCountry: 'اختر الدولة',
    phoneNumber: 'رقم الهاتف',
    message: 'الرسالة',
    sending: 'جارٍ الإرسال…',
    submit: 'إرسال',
    turnstileRequired: 'يرجى إكمال التحقق الأمني قبل الإرسال.',
    turnstileFailed: 'فشل التحقق الأمني. يرجى المحاولة مرة أخرى.',
    messagePlaceholder: (searchTerm: string) => `أنا مهتم بالمنتج: ${searchTerm}.`,
  },
}

export function ContactForm({
  productEnquiry = false,
  searchTerm = '',
}: {
  productEnquiry?: boolean
  searchTerm?: string
}) {
  const { locale } = useLocale()
  const router = useRouter()
  const t = CONTACT_FORM_TEXT[(locale as AppLocale) ?? 'en']
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [selectedCountryCode, setSelectedCountryCode] = useState('')
  const [formValues, setFormValues] = useState<ContactFormValues>(EMPTY_FORM_VALUES)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const formSubmitted = useRef(false)
  const turnstileRef = useRef<TurnstileWidgetHandle>(null)

  useEffect(() => {
    if (formSubmitted.current) return
    if (!productEnquiry) return

    const prefill = getProfilePrefill()
    const message = searchTerm ? t.messagePlaceholder(searchTerm) : ''

    setSelectedCountryCode(prefill.countryCode)
    setFieldErrors({})
    setFormValues({
      firstName: prefill.firstName,
      lastName: prefill.lastName,
      email: prefill.email,
      company: prefill.company,
      contactNumber: prefill.contactNumber,
      message,
    })
  }, [searchTerm, productEnquiry, t])

  useEffect(() => {
    formSubmitted.current = false
  }, [searchTerm])

  function updateField<K extends keyof ContactFormValues>(field: K, value: ContactFormValues[K]) {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  function clearForm() {
    formSubmitted.current = true
    setFormValues({ ...EMPTY_FORM_VALUES })
    setSelectedCountryCode('')
    setFieldErrors({})
  }

  function validate(
    firstName: string,
    lastName: string,
    email: string,
    company: string,
    countryCode: string,
    phoneLocal: string,
    message: string,
  ): boolean {
    const errors: FieldErrors = {}
    if (!firstName.trim()) {
      errors.firstName = t.firstNameRequired
    } else if (!isValidContactName(firstName)) {
      errors.firstName = t.firstNameInvalid
    }
    if (!lastName.trim()) {
      errors.lastName = t.lastNameRequired
    } else if (!isValidContactName(lastName)) {
      errors.lastName = t.lastNameInvalid
    }
    if (!email.trim()) {
      errors.email = t.emailRequired
    } else if (!isValidContactEmail(email)) {
      errors.email = t.emailInvalid
    }
    if (productEnquiry) {
      const trimmedCompany = company
      if (!trimmedCompany) {
        errors.company = t.companyRequired
      } else if (trimmedCompany.length < MIN_COMPANY_LENGTH) {
        errors.company = t.companyRequired
      } else if (trimmedCompany.length > MAX_COMPANY_LENGTH) {
        errors.company = t.companyTooLong
      } else if (!isValidContactCompany(trimmedCompany)) {
        errors.company = t.companyInvalid
      }
    }
    if (!countryCode.trim()) {
      errors.country = t.countryRequired
    }
    if (!phoneLocal.trim()) {
      errors.contactNumber = t.phoneRequired
    } else if (!PHONE_DIGITS_ONLY_REGEX.test(phoneLocal)) {
      errors.contactNumber = t.phoneInvalid
    }
    if (!message.trim()) {
      errors.message = t.messageRequired
    } else if (message.trim().length < MIN_MESSAGE_LENGTH) {
      errors.message = t.messageMinLength(MIN_MESSAGE_LENGTH)
    } else if (!isValidContactMessage(message)) {
      errors.message = t.messageInvalid
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setFieldErrors({})
    const { firstName, lastName, email, company, contactNumber, message } = formValues
    const countryOption = COUNTRIES.find((c) => c.code === selectedCountryCode)
    const countryName = countryOption?.name ?? selectedCountryCode
    const dialCode = countryOption?.dialCode ?? ''
    const fullContactNumber =
      dialCode && contactNumber ? `${dialCode} ${contactNumber.replace(/\s/g, '')}` : contactNumber

    if (
      !validate(firstName, lastName, email, company, selectedCountryCode, contactNumber, message)
    ) {
      setStatus('error')
      return
    }

    if (!turnstileToken) {
      toast.error(t.turnstileRequired)
      return
    }

    setStatus('sending')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${firstName} ${lastName}`.trim(),
          firstName,
          lastName,
          email,
          ...(productEnquiry ? { company } : {}),
          country: countryName,
          contactNumber: fullContactNumber,
          message,
          type: productEnquiry ? 'product' : 'contact',
          productCode: productEnquiry ? searchTerm : undefined,
          turnstileToken,
        }),
      })
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
      if (!res.ok || data.success === false) {
        const errorMsg = getApiErrorMessage(data, t.failedToSend)
        setStatus('idle')
        setTurnstileToken(null)
        turnstileRef.current?.reset()
        toast.error(errorMsg)
        return
      }
      const successMsg = typeof data.message === 'string' ? data.message : t.successMessage
      clearForm()
      setTurnstileToken(null)
      turnstileRef.current?.reset()
      setStatus('idle')
      toast.success(successMsg)
      if (productEnquiry) {
        router.replace('/products')
      }
    } catch {
      setStatus('idle')
      setTurnstileToken(null)
      turnstileRef.current?.reset()
      toast.error(t.failedToSend)
    }
  }

  const inputBase =
    'w-full rounded border border-black bg-white px-4 py-3 font-poppins text-sm text-black placeholder:text-neutral-500 focus:outline-none focus:ring-1 disabled:opacity-60'
  const inputError = 'border-red-500 focus:border-red-500 focus:ring-red-500'
  const inputOk = 'focus:border-[#E40520] focus:ring-[#E40520]'

  function clearFieldError(field: keyof FieldErrors) {
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  function clearFieldErrorIfValid(field: keyof FieldErrors, value: string) {
    const trimmed = value.trim()
    let isValid = false
    switch (field) {
      case 'firstName':
        isValid = trimmed.length > 0 && isValidContactName(value)
        break
      case 'lastName':
        isValid = trimmed.length > 0 && isValidContactName(value)
        break
      case 'email':
        isValid = trimmed.length > 0 && isValidContactEmail(value)
        break
      case 'company':
        isValid =
          value.length >= MIN_COMPANY_LENGTH &&
          value.length <= MAX_COMPANY_LENGTH &&
          isValidContactCompany(value)
        break
      case 'country':
        isValid = trimmed.length > 0
        break
      case 'contactNumber':
        isValid = trimmed.length > 0 && PHONE_DIGITS_ONLY_REGEX.test(value)
        break
      case 'message':
        isValid = trimmed.length >= MIN_MESSAGE_LENGTH && isValidContactMessage(value)
        break
      default:
        return
    }
    if (isValid) {
      clearFieldError(field)
    }
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      dir={locale == 'ar' ? 'rtl' : 'ltr'}
    >
      <h3
        className="font-bebas-neue mb-2 text-xl font-normal uppercase leading-tight text-[#1a1a1a] sm:text-2xl"
        style={productEnquiry ? { textAlign: 'center' } : {}}
      >
        {productEnquiry ? t.requestProduct : t.sendYourMessage}
      </h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="sr-only">{t.firstName}</span>
          <input
            id="contact-firstName"
            type="text"
            name="firstName"
            value={formValues.firstName}
            placeholder={t.firstName}
            required
            disabled={status === 'sending'}
            autoComplete="off"
            aria-invalid={Boolean(fieldErrors.firstName)}
            aria-describedby={fieldErrors.firstName ? 'contact-firstName-error' : undefined}
            className={`${inputBase} ${fieldErrors.firstName ? inputError : inputOk}`}
            onChange={(e) => {
              const value = sanitizeContactNameInput(e.target.value)
              updateField('firstName', value)
              clearFieldErrorIfValid('firstName', value)
            }}
          />
          {fieldErrors.firstName && (
            <p
              id="contact-firstName-error"
              className="mt-1.5 text-sm font-medium text-red-600"
              role="alert"
            >
              {fieldErrors.firstName}
            </p>
          )}
        </label>
        <label className="block">
          <span className="sr-only">{t.lastName}</span>
          <input
            id="contact-lastName"
            type="text"
            name="lastName"
            value={formValues.lastName}
            placeholder={t.lastName}
            required
            disabled={status === 'sending'}
            autoComplete="off"
            aria-invalid={Boolean(fieldErrors.lastName)}
            aria-describedby={fieldErrors.lastName ? 'contact-lastName-error' : undefined}
            className={`${inputBase} ${fieldErrors.lastName ? inputError : inputOk}`}
            onChange={(e) => {
              const value = sanitizeContactNameInput(e.target.value)
              updateField('lastName', value)
              clearFieldErrorIfValid('lastName', value)
            }}
          />
          {fieldErrors.lastName && (
            <p
              id="contact-lastName-error"
              className="mt-1.5 text-sm font-medium text-red-600"
              role="alert"
            >
              {fieldErrors.lastName}
            </p>
          )}
        </label>
      </div>
      <label className="block">
        <span className="sr-only">{t.email}</span>
        <input
          id="contact-email"
          type="email"
          name="email"
          value={formValues.email}
          placeholder={t.email}
          required
          disabled={status === 'sending'}
          autoComplete="off"
          aria-invalid={Boolean(fieldErrors.email)}
          aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
          className={`${inputBase} ${fieldErrors.email ? inputError : inputOk}`}
          onChange={(e) => {
            updateField('email', e.target.value)
            clearFieldErrorIfValid('email', e.target.value)
          }}
        />
        {fieldErrors.email && (
          <p
            id="contact-email-error"
            className="mt-1.5 text-sm font-medium text-red-600"
            role="alert"
          >
            {fieldErrors.email}
          </p>
        )}
      </label>
      {productEnquiry && (
        <label className="block">
          <span className="sr-only">{t.company}</span>
          <input
            id="contact-company"
            type="text"
            name="company"
            value={formValues.company}
            placeholder={t.company}
            required
            disabled={status === 'sending'}
            autoComplete="off"
            aria-invalid={Boolean(fieldErrors.company)}
            aria-describedby={fieldErrors.company ? 'contact-company-error' : undefined}
            className={`${inputBase} ${fieldErrors.company ? inputError : inputOk}`}
            onChange={(e) => {
              const value = sanitizeContactCompanyInput(e.target.value)
              updateField('company', value)
              clearFieldErrorIfValid('company', value)
            }}
          />
          {fieldErrors.company && (
            <p
              id="contact-company-error"
              className="mt-1.5 text-sm font-medium text-red-600"
              role="alert"
            >
              {fieldErrors.company}
            </p>
          )}
        </label>
      )}
      <label className="block">
        <span className="sr-only">{t.country}</span>
        <CountrySelect
          id="contact-country"
          name="country"
          required
          disabled={status === 'sending'}
          value={selectedCountryCode}
          onChange={(code) => {
            setSelectedCountryCode(code)
            clearFieldErrorIfValid('country', code)
          }}
          error={Boolean(fieldErrors.country)}
          aria-invalid={Boolean(fieldErrors.country)}
          aria-describedby={fieldErrors.country ? 'contact-country-error' : undefined}
          placeholder={t.selectCountry}
        />
        {fieldErrors.country && (
          <p
            id="contact-country-error"
            className="mt-1.5 text-sm font-medium text-red-600"
            role="alert"
          >
            {fieldErrors.country}
          </p>
        )}
      </label>
      <label className="block">
        <span className="sr-only">{t.phoneNumber}</span>
        <div
          className={`flex rounded overflow-hidden focus-within:ring-1 ${
            fieldErrors.contactNumber
              ? 'border border-red-500 focus-within:ring-red-500 focus-within:border-red-500'
              : 'border border-black focus-within:ring-[#E40520] focus-within:border-[#E40520]'
          }`}
        >
          <span dir="ltr"
            className={`flex min-w-[3rem] items-center bg-white px-4 py-3 font-poppins text-sm border-r border-black ${
              selectedCountryCode ? 'text-black' : 'text-neutral-400'
            }`}
            aria-hidden
          >
            {COUNTRIES.find((c) => c.code === selectedCountryCode)?.dialCode || '—'}
          </span>
          <input
            id="contact-contactNumber"
            type="tel"
            dir={locale == 'ar' ? 'rtl' : 'ltr'}
            name="contactNumber"
            value={formValues.contactNumber}
            placeholder={t.phoneNumber}
            required
            disabled={status === 'sending'}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            aria-invalid={Boolean(fieldErrors.contactNumber)}
            aria-describedby={fieldErrors.contactNumber ? 'contact-contactNumber-error' : undefined}
            className={`flex-1 min-w-0 border-0 bg-white px-4 py-3 font-poppins text-sm text-[#1a1a1a] placeholder:text-neutral-500 focus:outline-none focus:ring-0 ${fieldErrors.contactNumber ? inputError : ''}`}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '')
              updateField('contactNumber', value)
              clearFieldErrorIfValid('contactNumber', value)
            }}
          />
        </div>
        {fieldErrors.contactNumber && (
          <p
            id="contact-contactNumber-error"
            className="mt-1.5 text-sm font-medium text-red-600"
            role="alert"
          >
            {fieldErrors.contactNumber}
          </p>
        )}
      </label>
      <label className="block">
        <span className="sr-only">{t.message}</span>
        <textarea
          id="contact-message"
          name="message"
          value={formValues.message}
          placeholder={t.message}
          rows={5}
          required
          minLength={MIN_MESSAGE_LENGTH}
          disabled={status === 'sending'}
          aria-invalid={Boolean(fieldErrors.message)}
          aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
          className={`resize-y ${inputBase} ${fieldErrors.message ? inputError : inputOk}`}
          onChange={(e) => {
            updateField('message', e.target.value)
            clearFieldErrorIfValid('message', e.target.value)
          }}
        />
        {fieldErrors.message && (
          <p
            id="contact-message-error"
            className="mt-1.5 text-sm font-medium text-red-600"
            role="alert"
          >
            {fieldErrors.message}
          </p>
        )}
      </label>
      <TurnstileWidget
        ref={turnstileRef}
        action={productEnquiry ? 'product' : 'contact'}
        onVerify={setTurnstileToken}
        onExpire={() => setTurnstileToken(null)}
        className="flex justify-start"
      />
      <div className={`flex ${productEnquiry ? 'place-content-center' : 'place-content-start'}`}>
        <button
          type="submit"
          disabled={status === 'sending'}
          className="about-us-btn mt-8 inline-flex items-center justify-center gap-[10px] self-start
          bg-[#E40520] text-white
          w-[174.64px] h-[66px]
          px-[33px] py-[21px]
          font-semibold uppercase text-xs tracking-wide
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E40520] focus-visible:ring-offset-2
          disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'sending' ? t.sending : t.submit}
          {locale == 'ar' ? (
            <LuArrowUpLeft size={18} aria-hidden className="shrink-0 about-us-btn__arrow" />
          ) : (
            <LuArrowUpRight size={18} aria-hidden className="shrink-0 about-us-btn__arrow" />
          )}
        </button>
      </div>
    </form>
  )
}
